import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Clock } from "lucide-react";
import { searchArticles, catLabel, catAccent, timeAgo } from "@/lib/api";

export default function SearchModal({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setResults([]);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    setSearching(true);
    const t = setTimeout(() => {
      searchArticles(q)
        .then(setResults)
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start justify-center pt-24 px-4"
          onClick={onClose}
          data-testid="search-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: -24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -24, scale: 0.98 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-2xl bg-[#15151A] border border-white/15"
            onClick={(e) => e.stopPropagation()}
            data-testid="search-modal"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
              <Search size={18} className="text-[#FFD400] flex-shrink-0" />
              <input
                ref={inputRef}
                data-testid="search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="खबर खोजें..."
                className="flex-1 bg-transparent text-white text-lg focus:outline-none placeholder:text-slate-500"
              />
              <button onClick={onClose} data-testid="search-close-button" className="p-1 text-slate-400 hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>
            <div className="max-h-[55vh] overflow-y-auto">
              {searching && (
                <p className="px-5 py-6 font-mono-meta text-xs text-slate-500 uppercase tracking-widest">खोज रहे हैं...</p>
              )}
              {!searching && q.trim() && results.length === 0 && (
                <p className="px-5 py-6 text-sm text-slate-400">"{q}" के लिए कोई परिणाम नहीं मिला</p>
              )}
              {!searching &&
                results.map((a) => (
                  <Link
                    key={a.id}
                    to={`/article/${a.id}`}
                    onClick={onClose}
                    data-testid={`search-result-${a.id}`}
                    className="flex gap-4 px-5 py-4 border-b border-white/[0.06] hover:bg-white/[0.03] transition-colors group"
                  >
                    <img src={a.image_url} alt="" className="w-20 h-14 object-cover border border-white/10 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white group-hover:text-[#FFD400] transition-colors line-clamp-2">{a.title}</p>
                      <p className="font-mono-meta text-[10px] mt-1.5 flex items-center gap-2">
                        <span style={{ color: catAccent(a.category) }}>{catLabel(a.category)}</span>
                        <span className="text-slate-500 flex items-center gap-1"><Clock size={9} /> {timeAgo(a.created_at)}</span>
                      </p>
                    </div>
                  </Link>
                ))}
              {!q.trim() && (
                <p className="px-5 py-6 font-mono-meta text-[11px] text-slate-500 uppercase tracking-widest">
                  शीर्षक, सारांश या सामग्री से खोजें
                </p>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
